import {
    isSilentError,
    logError,
    signal,
    type LogTarget,
    type Unsubscribe,
} from '@amazon/vinyl-util'
import { type MutableValue } from '@amazon/vinyl-observable'

const errorHandlerTarget: LogTarget = {
    logPrefix: 'errorHandler',
}

export type ErrorReport = { target: LogTarget; error: unknown }

/**
 * Formats an unknown catch/reject value into one readable log string: the
 * `stack` (message + trace) for an `Error`, JSON for a plain object, else
 * `String(err)`. A raw `Error` handed straight to the logger serializes to an
 * opaque `[object Object]` once the console bridge / minified bundle stringifies
 * it (STYLE_GUIDE §5); this keeps the message and trace legible. Lives here —
 * the lowest error layer — so both `handleError` and util-level loggers can
 * reach it without a `errorHandler → util` back-edge.
 */
export function formatError(err: unknown): string {
    if (err instanceof Error) return err.stack ?? `${err.name}: ${err.message}`
    if (typeof err === 'object' && err !== null) {
        try {
            return JSON.stringify(err)
        } catch {
            return `{${Object.keys(err).join(', ')}}`
        }
    }
    return String(err)
}

// Errors are broadcast on a signal rather than errorHandler reaching into the
// UI layer: errorHandler is foundational (every `ctx()` builds one) while the
// toast pulls in `@/components/toast` → Icon → back into `ctx`, so a direct
// import would form a module-eval cycle. Consumers (the debug-gated toast and
// the bug-report diagnostics capture) subscribe via `onError` at bootstrap.
const errorSignal = signal<ErrorReport>()

/** Subscribe to non-silent errors routed through `handleError`. */
export function onError(cb: (report: ErrorReport) => void): Unsubscribe {
    return errorSignal.listen(cb)
}

export function handleError(target: LogTarget, error: unknown): void {
    if (isSilentError(error)) return
    logError(target, formatError(error))
    errorSignal.dispatch({ target, error })
}

export function createErrorHandler(target: LogTarget): (error: unknown) => void {
    return (error) => handleError(target, error)
}

/**
 * `.catch` handler for a detail-page section fetch: logs + captures the error
 * and stores it on `err$` so the section's own inline surface (retry row)
 * paints. Does not toast on its own — the section's inline surface is the
 * user-visible message.
 */
export function reportToInline(
    target: LogTarget,
    err$: MutableValue<Error | null>
): (error: Error) => void {
    return (error) => {
        handleError(target, error)
        err$.value = error
    }
}

export function initializeGlobalErrorHandling(): void {
    // Use raw addEventListener; errorHandler is the lowest layer (global error
    // capture) and must not depend on util to avoid a module cycle.
    window.addEventListener('error', (event) => {
        if (!(event.error instanceof Error)) return
        handleError(errorHandlerTarget, event.error)
    })
    window.addEventListener('unhandledrejection', (event) => {
        handleError(errorHandlerTarget, event.reason)
    })
}
